import { useNavigate } from "react-router-dom";
import { STATUS } from "@/lib/organs";

const P = {
  consciousness: { x: 160, y: 20 },
  memory: { x: 52, y: 62 },
  dna: { x: 54, y: 310 },
  eyes_ears: { x: 150, y: 48 },
  brain: { x: 150, y: 78 },
  nervous_system: { x: 150, y: 172 },
  heart: { x: 126, y: 150 },
  breathing: { x: 176, y: 150 },
  circulation: { x: 150, y: 208 },
  digestive: { x: 150, y: 242 },
  muscles: { x: 71, y: 184 },
  immune: { x: 231, y: 184 },
  skeleton: { x: 150, y: 392 },
};

// PEND-PW-01 fase 2: colore hotspot dalla proiezione census (census_status), MAI legacy status.
// Fallback esplicito GO §9: SYNC_DOT (tecnico), non grey legacy silenzioso.
const SYNC_DOT = { color: "#38BDF8", glow: "rgba(56,189,248,0.4)" };

function Hotspot({ organ, x, y, r = 9, onOpen }) {
  const st = STATUS[organ.census_status] || SYNC_DOT;
  return (
    <g data-testid={`organ-hotspot-${organ.organ_id}`} onClick={() => onOpen(organ.route)}
      className="cursor-pointer" role="button" aria-label={`${organ.anatomical_name} — ${organ.business_function}`}>
      <circle cx={x} cy={y} r={r + 8} fill={st.color} opacity={0.1}>
        <animate attributeName="r" values={`${r + 5};${r + 13};${r + 5}`} dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.18;0.04;0.18" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx={x} cy={y} r={r} fill={st.color} opacity={0.95}
        style={{ filter: `drop-shadow(0 0 7px ${st.glow})` }} />
      <circle cx={x} cy={y} r={r - 4} fill="#030712" opacity={0.55} />
      <title>{`${organ.anatomical_name} — ${organ.business_function}`}</title>
    </g>
  );
}

export default function Organism({ organs }) {
  const navigate = useNavigate();
  const byId = Object.fromEntries((organs || []).map((o) => [o.organ_id, o]));
  const open = (route) => route && navigate(route);

  return (
    <svg viewBox="0 0 320 470" className="w-full max-w-[340px] mx-auto" data-testid="company-organism">
      <defs>
        <linearGradient id="bodyFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#12203d" />
          <stop offset="100%" stopColor="#0a0f1d" />
        </linearGradient>
        <radialGradient id="aura" cx="50%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.07" />
          <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="320" height="470" fill="url(#aura)" rx="24" />

      {/* nervous links */}
      <g stroke="#00E5FF" strokeOpacity="0.14" strokeWidth="1">
        <line x1="150" y1="78" x2="126" y2="150" />
        <line x1="150" y1="78" x2="176" y2="150" />
        <line x1="150" y1="78" x2="150" y2="172" />
        <line x1="150" y1="172" x2="150" y2="242" />
        <line x1="150" y1="172" x2="71" y2="184" />
        <line x1="150" y1="172" x2="231" y2="184" />
        <line x1="150" y1="242" x2="150" y2="392" />
        <line x1="150" y1="60" x2="52" y2="62" />
        <line x1="120" y1="290" x2="54" y2="310" />
        <line x1="160" y1="20" x2="150" y2="42" />
      </g>

      {/* body silhouette */}
      <g fill="url(#bodyFill)" stroke="#00E5FF" strokeOpacity="0.22" strokeWidth="1">
        <circle cx="150" cy="62" r="34" />
        <rect x="102" y="104" width="96" height="160" rx="46" />
        <rect x="58" y="116" width="26" height="142" rx="13" />
        <rect x="218" y="116" width="26" height="142" rx="13" />
        <rect x="113" y="262" width="30" height="186" rx="15" />
        <rect x="157" y="262" width="30" height="186" rx="15" />
      </g>
      <line x1="150" y1="104" x2="150" y2="448" stroke="#00E5FF" strokeOpacity="0.18" strokeWidth="1.5" strokeDasharray="3 6" />

      {/* consciousness node */}
      <g data-testid="organ-hotspot-consciousness" onClick={() => open("/actions")} className="cursor-pointer" role="button">
        <circle cx="160" cy="20" r="13" fill="none" stroke="#9DF3FF" strokeOpacity="0.5" strokeDasharray="2 3" />
        <circle cx="160" cy="20" r="6" fill="#E0F7FF" style={{ filter: "drop-shadow(0 0 8px rgba(224,247,255,0.8))" }} />
        <text x="160" y="6" textAnchor="middle" fill="#6B7280" fontSize="7" letterSpacing="2">COSCIENZA</text>
        <title>Coscienza — Imprenditore · decide e autorizza</title>
      </g>

      {["eyes_ears", "brain", "heart", "breathing", "circulation", "digestive", "nervous_system", "muscles", "immune", "skeleton", "memory", "dna"].map((id) =>
        byId[id] ? <Hotspot key={id} organ={byId[id]} x={P[id].x} y={P[id].y} onOpen={open} /> : null
      )}

      <text x="52" y="44" textAnchor="middle" fill="#4B5563" fontSize="7" letterSpacing="1.5">MEMORIA</text>
      <text x="54" y="330" textAnchor="middle" fill="#4B5563" fontSize="7" letterSpacing="1.5">DNA</text>
    </svg>
  );
}
